export const MONEY_KINDS = ["salary", "advance", "repay", "bonus", "deduction"];
export const LEAVE_KINDS = ["day_off", "sick", "vacation", "absence", "late"];
export const KIND_TONE = { payroll: "#0B2A5B", salary: "#1F9D55", advance: "#E67E22", repay: "#2B79C2", bonus: "#1F9D55", deduction: "#C7353B", day_off: "#2B79C2", sick: "#B86E1F", vacation: "#1F9D55", absence: "#C7353B", late: "#E67E22" };

export const ym = (d) => (d || "").slice(0, 7);
export const today = () => new Date().toISOString().slice(0, 10);

export function daysBetween(a, b) {
  if (!a) return 0;
  const d1 = new Date(a), d2 = new Date(b || a);
  return Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
}

export function staffEvents(all, staffId) {
  return Object.values(all).filter((e) => e.staff_id === staffId && !e.deleted).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
}

export function staffDebt(evs) {
  return Math.round(evs.reduce((s, e) => s + (e.kind === "advance" ? e.amount : e.kind === "repay" ? -e.amount : 0), 0) * 100) / 100;
}

export function monthSummary(member, evs, month) {
  const m = evs.filter((e) => ym(e.date) === month);
  const sum = (k) => m.filter((e) => e.kind === k).reduce((s, e) => s + e.amount, 0);
  const mult = member.salary_type === "daily" ? 26 : member.salary_type === "weekly" ? 4.33 : 1;
  const base = Math.round(Number(member.salary || 0) * mult);
  const bonuses = sum("bonus"), deductions = sum("deduction"), advances = sum("advance"), paid = sum("salary"), repaid = sum("repay");
  const debt = staffDebt(evs);
  const net = Math.max(0, Math.round((base + bonuses - deductions - debt - paid - repaid) * 100) / 100);
  const payroll = evs.find((e) => e.kind === "payroll" && e.month === month) || null;
  return { base, bonuses, deductions, advances, paid, repaid, debt, net, month, payroll };
}

// Owner-defined payroll breakdown (desglose) → total to pay. Never negative: shortfall is reported separately.
export function payrollTotals(b) {
  const n = (x) => Math.round((Number(x) || 0) * 100) / 100;
  const gross = n(b.base) + n(b.incentives) + n(b.other);
  const deductions = n(b.deductions) + n(b.debt_deduct);
  const raw = Math.round((gross - deductions) * 100) / 100;
  return { gross, deductions, total: Math.max(0, raw), shortfall: raw < 0 ? -raw : 0 };
}

// Unique payslip code: initials + birth date + amount, with a checksum
export function payrollCode(member, month, amount) {
  const initials = (member.name || "").split(/\s+/).filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 3) || "X";
  const bd = (member.birth_date || "").replace(/-/g, "").slice(2) || "000000";
  const amt = String(Math.round(Number(amount) || 0));
  const src = `${initials}|${bd}|${amt}|${month}|${member.id}`;
  let h = 0x811c9dc5; for (let i = 0; i < src.length; i++) h = Math.imul(h ^ src.charCodeAt(i), 16777619) >>> 0;
  return `NOM-${initials}${bd}-${amt}-${h.toString(16).toUpperCase().padStart(8, "0").slice(0, 4)}`;
}

export function leaveStats(evs, month) {
  const year = month.slice(0, 4);
  const cnt = (k, scope) => evs.filter((e) => e.kind === k && (scope === "m" ? ym(e.date) === month : (e.date || "").startsWith(year))).reduce((s, e) => s + (e.days || 1), 0);
  return { dayOffM: cnt("day_off", "m"), sickY: cnt("sick", "y"), vacY: cnt("vacation", "y"), absM: cnt("absence", "m"), lateM: cnt("late", "m") };
}

export function onLeave(evs, day) {
  return evs.find((e) => LEAVE_KINDS.includes(e.kind) && e.kind !== "late" && e.date <= day && (e.date_to || e.date) >= day);
}

export function attendance(shifts, name, month) {
  const list = Object.values(shifts).filter((s) => s.opened_by === name && ym(s.opened_at) === month);
  const hours = list.reduce((s, x) => s + (x.closed_at ? (new Date(x.closed_at) - new Date(x.opened_at)) / 3600000 : 0), 0);
  const last = Object.values(shifts).filter((s) => s.opened_by === name).sort((a, b) => b.opened_at.localeCompare(a.opened_at))[0];
  const days = new Set(list.map((s) => s.opened_at.slice(0, 10))).size;
  return { count: list.length, hours: Math.round(hours * 10) / 10, days, last };
}

export const openClock = (att, staffId) => Object.values(att || {}).find((a) => a.staff_id === staffId && !a.out_at);

export function clockStats(att, staffId, month) {
  const all = Object.values(att || {}).filter((a) => a.staff_id === staffId).sort((a, b) => b.in_at.localeCompare(a.in_at));
  const list = all.filter((a) => ym(a.in_at) === month);
  const hrs = (a) => ((a.out_at ? new Date(a.out_at) : new Date()) - new Date(a.in_at)) / 3600000;
  const hours = list.reduce((s, a) => s + hrs(a), 0);
  return { all, list, hours: Math.round(hours * 10) / 10, days: new Set(list.map((a) => a.in_at.slice(0, 10))).size, open: all.find((a) => !a.out_at), hrs };
}

export function seniority(hire) {
  if (!hire) return null;
  const months = Math.floor((Date.now() - new Date(hire)) / (30.44 * 86400000));
  return months >= 12 ? { years: Math.floor(months / 12) } : { months };
}
